<template>
  <div class="view-tasks">
    <!-- Debug toggle -->
    <div class="debug-toggle-bar">
      <label class="debug-toggle-label">
        <input type="checkbox" v-model="showDebugIds" class="debug-checkbox" />
        <q-icon name="bug_report" size="14px" />
        <span>Показать ID</span>
      </label>
      <div v-if="showDebugIds" class="debug-info-inline">
        work_group_id: <strong>{{ data?.body?.work_group_id }}</strong> | 
        work_group_dict_id: <strong>{{ data?.body?.work_group_dict_id }}</strong>
      </div>
    </div>
    
    <div v-if="loading" class="loading-container">
      <q-spinner color="primary" size="3em" />
    </div>
    
    <div v-else-if="tasks.length > 0" class="tasks-container">
      <q-list dense bordered separator>
        <q-item v-for="task in tasks" :key="task.task_dict_id" class="task-item">
          <q-item-section>
            <q-item-label class="task-name">{{ task.task_name }}</q-item-label>
            <q-item-label v-if="showDebugIds" caption class="debug-id-visible">task_dict_id: {{ task.task_dict_id }}</q-item-label>
            <q-item-label caption v-if="task.unit_name" class="task-unit">
              Ед. изм.: {{ task.unit_name }}
            </q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-btn
              icon="delete"
              flat
              dense
              round
              size="sm"
              color="negative"
              @click="onDeleteTask(task)"
              :loading="deletingTaskId === task.task_dict_id"
            >
              <q-tooltip>Удалить задачу из группы</q-tooltip>
            </q-btn>
          </q-item-section>
        </q-item>
      </q-list>
    </div>
    
    <div v-else class="empty-container">
      <q-icon name="info" size="48px" color="grey-5" />
      <p>Нет задач в этой группе работ</p>
    </div>
    
    <div class="modal-footer">
      <q-btn
        label="Добавить недостающие"
        color="positive"
        outline
        @click="onAddMissing"
        :loading="addingMissing"
        class="add-missing-btn"
      >
        <q-tooltip>Добавить недостающие задачи по умолчанию</q-tooltip>
      </q-btn>
      <q-space />
      <q-btn
        label="Закрыть"
        color="primary"
        flat
        @click="onClose"
      />
    </div>

    <!-- Диалог подтверждения удаления -->
    <q-dialog v-model="confirmDialog.show" persistent>
      <q-card style="min-width: 350px">
        <q-card-section>
          <div class="text-h6">{{ confirmDialog.title }}</div>
        </q-card-section>

        <q-card-section>
          {{ confirmDialog.message }}
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="confirmDialog.show = false" />
          <q-btn unelevated label="Удалить" color="negative" @click="onConfirmDelete" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { deleteWorkGroupTask, addMissingWorkGroupTasks } from "@/views/TierBlocks/services";
import { getWorkGroupTasks } from "@/views/TierWorkGroup/services";

export default {
  name: "view-work-group-tasks",
  props: ['data', 'closeModal'],
  data() {
    return {
      loading: false,
      tasks: [],
      deletingTaskId: null,
      addingMissing: false,
      showDebugIds: false,
      confirmDialog: {
        show: false,
        title: '',
        message: '',
        taskToDelete: null
      }
    }
  },
  methods: {
    async loadTasks() {
      if (!this.data?.body?.work_group_id) {
        this.tasks = [];
        return;
      }
      
      this.loading = true;
      const res = await getWorkGroupTasks(this.data.body.work_group_id);
      this.loading = false;
      if (!res) return;
      this.tasks = res;
    },
    onDeleteTask(task) {
      this.confirmDialog = {
        show: true,
        title: 'Подтверждение удаления',
        message: `Вы уверены, что хотите удалить задачу "${task.task_name}" из этой группы работ?`,
        taskToDelete: task
      };
    },
    async onConfirmDelete() {
      if (this.confirmDialog.taskToDelete) {
        await this.deleteTask(this.confirmDialog.taskToDelete);
        this.confirmDialog.show = false;
      }
    },
    async deleteTask(task) {
      if (!this.data?.body?.work_group_id) {
        this.$q.notify({
          type: 'negative',
          message: 'Ошибка: не указан ID группы работ'
        });
        return;
      }

      this.deletingTaskId = task.task_dict_id;
      
      try {
        const res = await deleteWorkGroupTask(
          this.data.body.work_group_id,
          task.task_dict_id
        );
        
        if (res) {
          this.$q.notify({
            type: 'positive',
            message: 'Задача успешно удалена'
          });
          
          // Обновляем список задач
          await this.loadTasks();
          
          // Вызываем callback если он есть
          if (this.data?.onDelete) {
            this.data.onDelete();
          }
        }
      } catch (error) {
        this.$q.notify({
          type: 'negative',
          message: 'Ошибка при удалении задачи'
        });
      } finally {
        this.deletingTaskId = null;
      }
    },
    async onAddMissing() {
      if (!this.data?.body?.work_group_id) {
        this.$q.notify({
          type: 'negative',
          message: 'Ошибка: не указан ID группы работ'
        });
        return;
      }

      if (!this.data?.body?.work_group_dict_id) {
        this.$q.notify({
          type: 'negative',
          message: 'Ошибка: не указан ID справочника группы работ'
        });
        return;
      }

      this.addingMissing = true;
      
      try {
        const res = await addMissingWorkGroupTasks(
          this.data.body.work_group_id,
          this.data.body.work_group_dict_id
        );
        
        if (res) {
          const addedCount = res.added_count || 0;
          
          if (addedCount > 0) {
            this.$q.notify({
              type: 'positive',
              message: `Добавлено задач: ${addedCount}`
            });
          } else {
            this.$q.notify({
              type: 'info',
              message: 'Все задачи уже добавлены'
            });
          }
          
          // Обновляем список задач
          await this.loadTasks();
          
          // Вызываем callback если он есть
          if (this.data?.onDelete) {
            this.data.onDelete();
          }
        }
      } catch (error) {
        this.$q.notify({
          type: 'negative',
          message: 'Ошибка при добавлении задач'
        });
      } finally {
        this.addingMissing = false;
      }
    },
    onClose() {
      this.closeModal?.();
    }
  },
  mounted() {
    this.loadTasks();
  }
}
</script>

<style scoped lang="scss">
.view-tasks {
  width: 500px;
  max-height: 600px;
  display: flex;
  flex-direction: column;
}

.loading-container,
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  gap: 16px;
  
  p {
    margin: 0;
    color: #999;
    font-size: 14px;
  }
}

.tasks-container {
  flex: 1;
  overflow-y: auto;
  max-height: 500px;
}

.task-item {
  padding: 12px;
}

.task-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.task-unit {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 16px;
  border-top: 1px solid #e0e0e0;
  margin-top: 16px;
}

@media (max-width: 600px) {
  .view-tasks {
    width: 100%;
  }
}

/* Debug styles */
.debug-toggle-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 12px;
  background: #fafafa;
  border-bottom: 1px solid #e0e0e0;
}

.debug-toggle-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #e65100;
  cursor: pointer;
  user-select: none;
}

.debug-toggle-label:hover {
  color: #bf360c;
}

.debug-checkbox {
  width: 14px;
  height: 14px;
  cursor: pointer;
}

.debug-info-inline {
  font-size: 11px;
  color: #e65100;
  font-family: 'Consolas', 'Monaco', monospace;
  background: #fff3e0;
  padding: 2px 8px;
  border-radius: 3px;
}

.debug-info-inline strong {
  font-weight: 700;
}

.debug-id-visible {
  font-size: 11px !important;
  color: #e65100 !important;
  font-family: 'Consolas', 'Monaco', monospace !important;
  font-weight: 600 !important;
  background: #fff3e0;
  padding: 1px 6px;
  border-radius: 2px;
  margin-top: 2px;
  display: inline-block;
}
</style>

